import styles from '../TaskExecution.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TaskDefinition, TaskExecutionType } from '../../../api/models/tasks';
import { calculateExecutionTime, formatDate } from '../../../components/miscUtils';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { markTaskExecutionAsRead, selectTaskExecution } from '../../../data/taskExecutionSlice';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { getAssetTypeName } from '../../../data/assets/helpers';

interface TaskCardListProps {
  taskList: TaskExecutionType[];
  selectedTask: TaskExecutionType | null;
  setSelectedTask: (arg0: TaskExecutionType) => void;
  taskDefinitions: TaskDefinition[];
}

const TaskCardList = (props: TaskCardListProps) => {
  const { taskList, selectedTask, setSelectedTask, taskDefinitions } = props;
  const taskExecutions = useAppSelector(selectTaskExecution);
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const dispatch = useAppDispatch();

  const statusIcons: any = {
    queued: <FontAwesomeIcon className={styles.queuedStatus} icon={['fas', 'timer']} />,
    running: <FontAwesomeIcon className={styles.inProgressStatus} icon={['fas', 'hourglass-half']} />,
    completed: <FontAwesomeIcon className={styles.completedStatus} icon={['fas', 'circle-check']} />,
    failed: <FontAwesomeIcon className={styles.failedStatus} icon={['fas', 'octagon-exclamation']} />,
    canceled: <FontAwesomeIcon className={styles.canceledStatus} icon={['fas', 'trash-can-xmark']} />,
  };

  function onCardClick(taskExecution: TaskExecutionType) {
    setSelectedTask(taskExecution);
  }

  const getTaskType = (taskDefinitionId: string) => {
    const taskDefinition = taskDefinitions.find(
      (taskDefinition) => taskDefinition.taskDefinitionId === taskDefinitionId,
    );
    return taskDefinition?.taskType;
  };

  useEffect(() => {
    if (selectedTask) {
      const taskExecution = taskExecutions.unreadTaskExecutionList.find(
        (taskExecution) => taskExecution.taskExecutionId === selectedTask.taskExecutionId,
      );
      if (taskExecution) {
        dispatch(markTaskExecutionAsRead({ taskExecutionId: selectedTask.taskExecutionId }));
      }
    }
  }, [selectedTask, taskExecutions.unreadTaskExecutionList]);

  const checkIsUnread = (taskExecutionId: string) => {
    return taskExecutions.unreadTaskExecutionList.some((unreadTask) => unreadTask.taskExecutionId === taskExecutionId);
  };

  return (
    <div className={styles.taskExecutionGrid}>
      {taskList.map((taskExecution: TaskExecutionType, index: number) => (
        <div
          className={`${styles.taskExecutionCard}  ${
            checkIsUnread(taskExecution.taskExecutionId) ? styles.unreadCard : ''
          } ${taskExecution.taskExecutionId == selectedTask?.taskExecutionId ? styles.activeCard : ''}`}
          key={index}
          onClick={() => {
            onCardClick(taskExecution);
          }}
        >
          <div className={styles.taskCardTitle}>
            {statusIcons[taskExecution.status]}
            <div className={styles.taskCardName}>{taskExecution.name}</div>
            {getTaskType(taskExecution.taskDefinitionId) === 'system' && (
              <div className={styles.taskBadgeOverview}>System task</div>
            )}
            {taskExecution.executingParameters?.resume_task_execution_id && (
              <div
                className={styles.taskBadgeOverview}
                style={{
                  backgroundColor: 'var(--highlightTextBg)',
                  color: 'var(--prim3-color-darker1)',
                  borderColor: 'var(--prim3-color-darker1)',
                }}
              >
                Resumed
              </div>
            )}
          </div>
          <div className={styles.cardSeparator} />
          {!!taskExecution.parentAssetType && (
            <div className={styles.taskCardValueLine}>
              Asset type:
              <div className={styles.taskCardValue}>
                {getAssetTypeName(ontology.assetTypes[taskExecution.parentAssetType!])}
              </div>
            </div>
          )}
          <div className={styles.taskCardValueLine}>
            Start date and time:
            <div className={styles.taskCardValue}>{formatDate(taskExecution.startTime) ?? taskExecution.startTime}</div>
          </div>
          {taskExecution.endTime && (
            <div className={styles.taskCardValueLine}>
              Execution time:
              <div className={styles.taskCardValue}>
                {calculateExecutionTime(new Date(taskExecution.startTime), new Date(taskExecution.endTime))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TaskCardList;
